import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API_BASE, downloadFile } from "@/lib/api";
import { fmtEUR, fmtEURSigned, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, FileDown, Mail, AlertTriangle, Inbox, FileText, Send, CheckCircle2, XCircle, Loader2, Clock, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useSortable } from "@/lib/sortable";
import { slaOverzichtNavOp } from "@/lib/overzichtNav";
import { usePersistedState } from "@/lib/persist";
import { Checkbox } from "@/components/ui/checkbox";
import { BankImport } from "@/components/BankImport";
import { TaalKeuzeDialog, useTaalActie } from "@/components/TaalKeuzeDialog";

// Batch-verzendtool: verstuurt geselecteerde wachtrij-facturen op volgorde van aanmaakdatum met live voortgang.
function BatchVerstuurDialog({ facturen, orgs, onDone }) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [klaar, setKlaar] = useState(false);
  const [statuses, setStatuses] = useState({});
  const [sel, setSel] = useState(() => new Set());

  // Sorteer op aanmaakdatum (oplopend)
  const lijst = useMemo(
    () => [...facturen].sort((a, b) => new Date(a.created_at || a.datum || 0) - new Date(b.created_at || b.datum || 0)),
    [facturen]
  );

  // Bij openen: standaard alles aanvinken
  useEffect(() => {
    if (open) setSel(new Set(lijst.map(f => f.id)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const teVersturen = useMemo(() => lijst.filter(f => sel.has(f.id)), [lijst, sel]);

  const toggle = (id) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const alleGeselecteerd = lijst.length > 0 && lijst.every(f => sel.has(f.id));
  const toggleAlle = () => setSel(alleGeselecteerd ? new Set() : new Set(lijst.map(f => f.id)));

  const start = async () => {
    setRunning(true); setKlaar(false);
    const init = {}; teVersturen.forEach(f => { init[f.id] = { state: "wachten" }; });
    setStatuses(init);
    for (const f of teVersturen) {
      setStatuses(prev => ({ ...prev, [f.id]: { state: "bezig" } }));
      try {
        const { data } = await api.post(`/verkoopfacturen/${f.id}/verstuur-email`, {});
        setStatuses(prev => ({ ...prev, [f.id]: { state: "ok", msg: data.naar } }));
      } catch (e) {
        setStatuses(prev => ({ ...prev, [f.id]: { state: "fout", msg: e?.response?.data?.detail || "Mislukt" } }));
      }
    }
    setRunning(false); setKlaar(true);
    onDone?.();
  };

  const done = teVersturen.filter(f => ["ok", "fout"].includes(statuses[f.id]?.state)).length;
  const okCount = teVersturen.filter(f => statuses[f.id]?.state === "ok").length;
  const foutCount = teVersturen.filter(f => statuses[f.id]?.state === "fout").length;
  const pct = teVersturen.length ? Math.round((done / teVersturen.length) * 100) : 0;

  const reset = (o) => { setOpen(o); if (!o) { setStatuses({}); setKlaar(false); } };

  const ICON = {
    wachten: <Clock className="h-4 w-4 text-muted-foreground" />,
    bezig: <Loader2 className="h-4 w-4 text-primary animate-spin" />,
    ok: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
    fout: <XCircle className="h-4 w-4 text-destructive" />,
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <Button onClick={() => setOpen(true)} disabled={!lijst.length} data-testid="batch-verstuur-open">
        <Send className="h-4 w-4 mr-1" /> Batch versturen{lijst.length ? ` (${lijst.length})` : ""}
      </Button>
      <DialogContent className="max-w-lg" data-testid="batch-verstuur-dialog">
        <DialogHeader>
          <DialogTitle>Facturen versturen in batch</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          Vink de facturen aan die je wilt versturen. Facturen die je pas wilt versturen zodra de zending is aangekomen, laat je uitgevinkt.
          De geselecteerde facturen worden per e-mail (Resend) verstuurd op volgorde van aanmaakdatum.
        </p>

        {!running && !klaar && (
          <div className="flex items-center justify-between text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="batch-select-all-label">
              <Checkbox checked={alleGeselecteerd} onCheckedChange={toggleAlle} data-testid="batch-select-all" />
              <span>Alles selecteren</span>
            </label>
            <span className="text-muted-foreground" data-testid="batch-selectie-telling">{teVersturen.length} van {lijst.length} geselecteerd</span>
          </div>
        )}

        {(running || klaar) && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{done} / {teVersturen.length} verwerkt</span>
              <span>{okCount} verzonden · {foutCount} mislukt</span>
            </div>
            <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} data-testid="batch-progress" />
            </div>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto divide-y divide-border border border-border rounded-lg">
          {lijst.map(f => {
            const st = statuses[f.id]?.state || "wachten";
            const actief = running || klaar;
            return (
              <div key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`batch-rij-${f.id}`}>
                {actief
                  ? ICON[st]
                  : <Checkbox checked={sel.has(f.id)} onCheckedChange={() => toggle(f.id)} data-testid={`batch-select-${f.id}`} />}
                <span className="font-mono text-xs w-24 shrink-0">{f.factuurnummer || f.id.slice(0, 8)}</span>
                <span className="flex-1 truncate">{f.organisatie_naam || orgs?.[f.relatie_id] || "—"}</span>
                {st === "fout" && <span className="text-xs text-destructive truncate max-w-[140px]">{statuses[f.id]?.msg}</span>}
                {st === "ok" && <span className="text-xs text-emerald-600 truncate max-w-[160px]">{statuses[f.id]?.msg}</span>}
              </div>
            );
          })}
          {!lijst.length && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Geen te versturen facturen in de wachtrij.</div>}
        </div>

        <DialogFooter>
          {!klaar ? (
            <>
              <Button variant="outline" onClick={() => reset(false)} disabled={running}>Annuleren</Button>
              <Button onClick={start} disabled={running || !teVersturen.length} data-testid="batch-verstuur-start">
                {running ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Bezig…</> : <><Send className="h-4 w-4 mr-1" /> Verstuur selectie ({teVersturen.length})</>}
              </Button>
            </>
          ) : (
            <Button onClick={() => reset(false)} data-testid="batch-verstuur-sluiten">Sluiten</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compacte Moneybird-batchknop: pusht alle nog niet-gesynchroniseerde facturen
// van de huidige tab naar Moneybird. Toont de resultatentelling in een toast.
function BatchMoneybirdKnop({ facturen, label }) {
  const [busy, setBusy] = useState(false);
  const kandidaten = useMemo(
    () => (facturen || []).filter(f => !(f.moneybird && (f.moneybird.external_sales_invoice_id || f.moneybird.sales_invoice_id))),
    [facturen]);

  const start = async () => {
    if (!kandidaten.length) { toast.info("Alle zichtbare facturen staan al in Moneybird."); return; }
    if (!window.confirm(`${kandidaten.length} factuur/facturen naar Moneybird pushen?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/moneybird/facturen/batch-push",
        { ids: kandidaten.map(f => f.id), verstuur: false });
      toast.success(`${data.gesynchroniseerd} nieuw geboekt · ${data.overgeslagen} al aanwezig · ${data.mislukt} mislukt`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch-push mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Button variant="outline" onClick={start} disabled={busy || !kandidaten.length}
            data-testid="moneybird-batch-push">
      <Building2 className="h-4 w-4 mr-1" />
      {busy ? "Bezig…" : `${label || "Naar Moneybird"}${kandidaten.length ? ` (${kandidaten.length})` : ""}`}
    </Button>
  );
}

// Werkelijke factuurstatussen in de DB
const WACHTRIJ_STATUSSEN = ["concept", "nog_te_factureren"];
const OPEN_STATUSSEN = ["verstuurd", "geboekt", "gefactureerd", "1e herinnering verstuurd", "2e herinnering verstuurd"];
const STATUS_LABELS = {
  concept: "Wachtrij", nog_te_factureren: "Wachtrij", geboekt: "Geboekt",
  verstuurd: "Verstuurd", betaald: "Betaald", gecrediteerd: "Gecrediteerd",
  deels_betaald: "Deels betaald",
  "1e herinnering verstuurd": "1e herinnering", "2e herinnering verstuurd": "2e herinnering",
};

// Categorie-indeling (wederzijds uitsluitend) voor staafdiagram + tabs
const classify = (it) => {
  if (WACHTRIJ_STATUSSEN.includes(it.status_factuur)) return "wachtrij";
  if (it.type === "creditfactuur" || it.status_factuur === "gecrediteerd") return "credit";
  if (it.status_factuur === "betaald") return "betaald";
  if (it.status_factuur === "deels_betaald") return "deels";
  if (it.vervaldatum && new Date(it.vervaldatum) < new Date()) return "vervallen";
  return "openstaand";
};

const CATS = [
  { k: "wachtrij", l: "Wachtrij", color: "#94a3b8" },
  { k: "openstaand", l: "Openstaand", color: "#3b82f6" },
  { k: "deels", l: "Deels betaald", color: "#f59e0b" },
  { k: "vervallen", l: "Vervallen", color: "#ef4444" },
  { k: "betaald", l: "Betaald", color: "#10b981" },
];

const betalingsbedrag = (it) =>
  it.status_factuur === "betaald" ? (it.bedrag_betaald ?? it.bedrag ?? 0) : (it.bedrag_betaald ?? 0);

const StatusBadge = ({ it }) => {
  const cat = classify(it);
  if (cat === "wachtrij") return <Badge variant="secondary">Wachtrij</Badge>;
  if (cat === "credit") return it.status_factuur === "verwerkt"
    ? <Badge className="bg-rose-600 text-white hover:bg-rose-600">Credit · verwerkt</Badge>
    : <Badge variant="destructive">Credit</Badge>;
  if (cat === "betaald") return <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">Betaald</Badge>;
  if (cat === "deels") return <Badge className="bg-amber-500 text-white hover:bg-amber-500">Deels betaald</Badge>;
  if (cat === "vervallen") return <Badge className="bg-destructive text-destructive-foreground">Vervallen</Badge>;
  return <Badge>{STATUS_LABELS[it.status_factuur] || "Gefactureerd"}</Badge>;
};

function VerstuurDialog({ factuur, open, onOpenChange, onSent }) {
  const [factuurnummer, setFactuurnummer] = useState(factuur?.factuurnummer || "");
  const [vervaldatum, setVervaldatum] = useState("");
  useEffect(() => {
    if (open) {
      setFactuurnummer(factuur?.factuurnummer || "");
      const d = new Date(); d.setDate(d.getDate() + 30);
      setVervaldatum(d.toISOString().slice(0, 10));
    }
  }, [open, factuur]);

  const verzend = async () => {
    try {
      const { data } = await api.post(`/verkoopfacturen/${factuur.id}/verstuur-email`, {
        factuurnummer,
        vervaldatum: vervaldatum ? new Date(vervaldatum).toISOString() : null,
      });
      toast.success(`Factuur ${data.factuurnummer} verstuurd naar ${data.naar}`);
      onSent();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt");
    }
  };

  if (!factuur) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Factuur versturen per e-mail</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm bg-secondary/50 rounded-md p-3 space-y-1">
            <div><span className="text-muted-foreground">Bedrag:</span> <strong>{fmtEURSigned(factuur.bedrag, factuur.type === "creditfactuur")}</strong></div>
            {factuur.ordernummer && <div><span className="text-muted-foreground">Ordernummer:</span> {factuur.ordernummer}</div>}
            {factuur.leveranciers_id && <div><span className="text-muted-foreground">Leveranciers-ID:</span> {factuur.leveranciers_id}</div>}
          </div>
          <div>
            <Label>Factuurnummer</Label>
            <Input data-testid="verstuur-nummer" value={factuurnummer} onChange={(e)=>setFactuurnummer(e.target.value)} placeholder="Bijv. 2026-0042" />
            <div className="text-xs text-muted-foreground mt-1">Leeg laten → automatisch gegenereerd</div>
          </div>
          <div>
            <Label>Vervaldatum</Label>
            <Input type="date" data-testid="verstuur-vervaldatum" value={vervaldatum} onChange={(e)=>setVervaldatum(e.target.value)} />
          </div>
          <div className="text-xs text-muted-foreground">
            Bij verzenden wordt de status op <strong>verstuurd</strong> gezet en wordt de factuur (met PDF-bijlage) automatisch per e-mail via Resend naar het factuur-e-mailadres van de klant gestuurd.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={verzend} data-testid="verstuur-submit">
            <Mail className="h-4 w-4 mr-2" /> Verstuur via e-mail
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FacturenTable({ rows, orgs, orgLand = {}, onVerstuur, showVerstuur = false, loading = false, onMailed }) {
  const navigate = useNavigate();
  const taalKeuze = useTaalActie();
  const { sortedRows, SortHeader } = useSortable(rows, "datum", "desc");
  const openFactuur = (fid) => {
    slaOverzichtNavOp("facturen", sortedRows.map(r => r.id), window.location.pathname + window.location.search);
    navigate(`/verkoopfacturen/${fid}`);
  };
  return (
    <div className="bg-card border border-border rounded-xl overflow-x-auto">
      <table className="w-full">
        <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
          <tr>
            <SortHeader k="datum">Factuurdatum</SortHeader>
            <SortHeader k="factuurnummer">Factuurnummer</SortHeader>
            <SortHeader k="status_factuur">Status</SortHeader>
            <SortHeader k="organisatie" accessor={(it)=>it.organisatie_naam||orgs[it.relatie_id]||""}>Organisatie</SortHeader>
            <SortHeader k="ordernummer">Ordernummer</SortHeader>
            <SortHeader k="bedrag" align="right" accessor={(it)=>Number(it.type==="creditfactuur"?-1:1) * Number(it.bedrag||0)}>Factuurbedrag</SortHeader>
            <SortHeader k="betaling" align="right" accessor={(it)=>Number(betalingsbedrag(it))}>Betalingsbedrag</SortHeader>
            <th></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedRows.map(it => {
            const betaald = betalingsbedrag(it);
            return (
              <tr key={it.id} className="hover:bg-secondary/40 cursor-pointer transition-colors"
                  onClick={() => openFactuur(it.id)}
                  data-testid={`factuur-row-${it.id}`}>
                <td className="p-3 text-muted-foreground whitespace-nowrap">{fmtDate(it.datum)}</td>
                <td className="p-3 font-medium">{it.factuurnummer || <span className="text-muted-foreground italic">concept</span>}</td>
                <td className="p-3"><StatusBadge it={it} /></td>
                <td className="p-3" onClick={(e)=>e.stopPropagation()}>
                  <Link to={`/organisaties/${it.relatie_id}`} className="text-primary hover:underline">
                    {it.organisatie_naam || orgs[it.relatie_id] || "—"}
                  </Link>
                </td>
                <td className="p-3 text-muted-foreground">{it.ordernummer || "—"}</td>
                <td className={`p-3 text-right font-medium ${it.type === "creditfactuur" ? "text-rose-700" : ""}`}>{fmtEURSigned(it.bedrag, it.type === "creditfactuur")}</td>
                <td className="p-3 text-right">
                  {betaald > 0
                    ? <span className={classify(it) === "deels" ? "text-amber-600 font-medium" : "text-emerald-600 font-medium"}>{fmtEUR(betaald)}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-3 text-right whitespace-nowrap" onClick={(e)=>e.stopPropagation()}>
                  <button onClick={() => taalKeuze.start((it.organisatie_land||orgLand[it.relatie_id]), (taal) => downloadFile(`/verkoopfacturen/${it.id}/docx?taal=${taal}`, `factuur-${it.factuurnummer||it.id.slice(0,8)}.docx`))}
                     className="inline-flex items-center text-primary hover:underline text-sm mr-2" data-testid={`factuur-docx-${it.id}`}>
                    <FileDown className="h-4 w-4 mr-1" /> Word
                  </button>
                  <button onClick={() => taalKeuze.start((it.organisatie_land||orgLand[it.relatie_id]), (taal) => downloadFile(`/verkoopfacturen/${it.id}/pdf?taal=${taal}`, `factuur-${it.factuurnummer||it.id.slice(0,8)}.pdf`))}
                     className="inline-flex items-center text-primary hover:underline text-sm mr-3" data-testid={`factuur-pdf-${it.id}`}>
                    <FileDown className="h-4 w-4 mr-1" /> PDF
                  </button>
                  <button onClick={() => taalKeuze.start((it.organisatie_land||orgLand[it.relatie_id]), async (taal) => { try { const { data } = await api.post(`/verkoopfacturen/${it.id}/verstuur-email`, { taal }); toast.success(`Factuur ${data.factuurnummer} gemaild naar ${data.naar}`); onMailed?.(); } catch(err){ toast.error(err?.response?.data?.detail || "Mailen mislukt"); } })}
                     className="inline-flex items-center text-primary hover:underline text-sm mr-3" data-testid={`factuur-mail-${it.id}`}>
                    <Mail className="h-4 w-4 mr-1" /> Mail
                  </button>
                  {showVerstuur && WACHTRIJ_STATUSSEN.includes(it.status_factuur) && (
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); onVerstuur(it); }} data-testid={`verstuur-btn-${it.id}`}>
                      <Mail className="h-3 w-3 mr-1" /> Verstuur
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
          {sortedRows.length === 0 && (
            <tr><td colSpan={8} className="p-10">
              {loading ? (
                <div className="flex flex-col items-center gap-3 text-muted-foreground" data-testid="facturen-tabel-laden">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-sm">Facturen laden…</span>
                  <div className="h-1.5 w-56 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full w-full bg-primary/60 rounded-full animate-pulse" />
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted-foreground">Geen facturen.</div>
              )}
            </td></tr>
          )}
        </tbody>
      </table>
      <TaalKeuzeDialog open={taalKeuze.open} onOpenChange={taalKeuze.setOpen} land={taalKeuze.land} onKies={taalKeuze.bevestig} titel="Kies de taal van de factuur" />
    </div>
  );
}

function VerhoudingBalk({ counts, bedragen, tab, onPick }) {
  const barCats = CATS.filter(c => c.k !== "betaald");
  const totaal = barCats.reduce((s, c) => s + (counts[c.k] || 0), 0);
  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="facturen-verhouding">
      <div className="flex h-7 w-full rounded-full overflow-hidden bg-secondary">
        {barCats.map(c => {
          const n = counts[c.k] || 0;
          if (!n) return null;
          const pct = totaal > 0 ? (n / totaal) * 100 : 0;
          return (
            <button key={c.k} onClick={() => onPick(c.k)} title={`${c.l}: ${n} · ${fmtEUR(bedragen[c.k] || 0)}`}
              data-testid={`balk-${c.k}`}
              className="h-full flex items-center justify-center overflow-hidden transition-opacity hover:opacity-90"
              style={{ width: `${pct}%`, backgroundColor: c.color, opacity: tab === "alle" || tab === c.k ? 1 : 0.45 }}>
              {pct >= 14 && <span className="text-[11px] font-semibold text-white whitespace-nowrap px-1">{fmtEUR(bedragen[c.k] || 0)}</span>}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
        {barCats.map(c => (
          <button key={c.k} onClick={() => onPick(c.k)} className="inline-flex items-center gap-1.5 hover:text-foreground text-muted-foreground" data-testid={`legenda-${c.k}`}>
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: c.color }} />
            {c.l} <span className="font-medium text-foreground">{counts[c.k] || 0}</span>
            <span className="text-muted-foreground">· {fmtEUR(bedragen[c.k] || 0)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Verkoopfacturen() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "wachtrij";
  // Onthoud de gekozen tab per sessie (herstel bij binnenkomst zonder ?tab)
  useEffect(() => { try { sessionStorage.setItem("crmstate:facturen:tab", tab); } catch {} }, [tab]);
  useEffect(() => {
    if (!params.get("tab")) {
      let saved; try { saved = sessionStorage.getItem("crmstate:facturen:tab"); } catch {}
      if (saved && saved !== tab) setParams({ tab: saved });
    }
    /* eslint-disable-next-line */
  }, []);
  const [items, setItems] = useState([]);
  const [totaal, setTotaal] = useState(0);
  const [page, setPage] = useState(1);
  const [counts, setCounts] = useState({});
  const [bedragen, setBedragen] = useState({});
  const [totaalAlle, setTotaalAlle] = useState(0);
  const [q, setQ] = usePersistedState("facturen:q", "");
  const [qDebounced, setQDebounced] = useState(q);
  const [verstuurFor, setVerstuurFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wachtrijItems, setWachtrijItems] = useState([]);
  const pageSize = 50;

  const laadTellingen = () => api.get("/verkoopfacturen/overzicht-tellingen")
    .then(({ data }) => { setCounts(data.counts || {}); setBedragen(data.bedragen || {}); setTotaalAlle(data.totaal || 0); })
    .catch(e => console.warn("Tellingen laden mislukt:", e));

  const laadWachtrij = () => api.get("/verkoopfacturen/pagina", { params: { tab: "wachtrij", page: 1, page_size: 500 } })
    .then(({ data }) => setWachtrijItems(data.items || []))
    .catch(() => setWachtrijItems([]));

  const laadPagina = async (p, zoek = qDebounced, huidigeTab = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get("/verkoopfacturen/pagina", { params: { tab: huidigeTab, q: zoek, page: p, page_size: pageSize } });
      setItems(data.items || []); setTotaal(data.totaal || 0); setPage(data.page || p);
    } catch (e) { console.warn("Pagina laden mislukt:", e); setItems([]); }
    finally { setLoading(false); }
  };

  const reloadAll = () => { laadTellingen(); if (tab === "wachtrij") laadWachtrij(); laadPagina(1); };

  // Tellingen éénmalig; wachtrijlijst alleen nodig voor de batch-knop op de wachtrij-tab
  useEffect(() => { laadTellingen(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (tab === "wachtrij" && !wachtrijItems.length) laadWachtrij(); /* eslint-disable-next-line */ }, [tab]);
  // Zoekterm debouncen (alleen de zoekactie, niet tab/paginawissel)
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  // Direct laden bij tab-/pagina-/zoekwijziging (geen kunstmatige vertraging)
  useEffect(() => { laadPagina(page); /* eslint-disable-next-line */ }, [tab, page, qDebounced]);

  const totalPages = Math.max(1, Math.ceil(totaal / pageSize));
  const rows = items;
  const totaalBedrag = rows.reduce((s, i) => s + (i.type === "creditfactuur" ? -1 : 1) * (i.bedrag || 0), 0);

  const TABS = [...CATS.map(c => ({ k: c.k, l: c.l, count: counts[c.k] || 0 })),
                { k: "alle", l: "Alle", count: totaalAlle }];

  return (
    <div className="space-y-6" data-testid="facturen-page">
      <div>
        <div className="text-sm text-muted-foreground">Verkoop</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Verkoopfacturen</h1>
        <p className="text-sm text-muted-foreground mt-1">{totaal} item(s){totalPages > 1 ? ` · pagina ${page}/${totalPages}` : ""} · deze pagina {fmtEUR(totaalBedrag)}</p>
      </div>

      <VerhoudingBalk counts={counts} bedragen={bedragen} tab={tab} onPick={(k) => { setParams({ tab: k }); setPage(1); }} />

      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map(({ k, l, count }) => (
          <button key={k} onClick={() => { setParams({ tab: k }); setPage(1); }} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${tab===k?"border-primary text-primary":"border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
            {count > 0 && <Badge variant="secondary" className="ml-1">{count}</Badge>}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl p-3 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Zoek op factuurnr, organisatie, ordernummer…" value={q} onChange={(e)=>{ setQ(e.target.value); setPage(1); }} data-testid="factuur-search" />
        </div>
        <BankImport onApplied={reloadAll} />
      </div>

      {tab === "wachtrij" && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="text-sm bg-accent/10 border border-accent/30 rounded-lg p-3 flex items-start gap-2 flex-1">
            <Inbox className="h-4 w-4 text-accent mt-0.5" />
            <div>Concept-facturen, automatisch gegenereerd na een pakbon. Verstuur ze los, of gebruik <strong>Batch versturen</strong> om ze allemaal per e-mail te versturen (op volgorde van aanmaakdatum).</div>
          </div>
          <BatchVerstuurDialog facturen={wachtrijItems} orgs={{}} onDone={reloadAll} />
          <BatchMoneybirdKnop facturen={rows} label="Zichtbare naar Moneybird" />
        </div>
      )}
      {tab === "alle" && rows.length > 0 && (
        <div className="flex justify-end">
          <BatchMoneybirdKnop facturen={rows} label="Zichtbare naar Moneybird" />
        </div>
      )}
      {tab === "vervallen" && (counts.vervallen || 0) > 0 && (
        <div className="text-sm bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
          <div>Deze facturen zijn over hun vervaldatum. Tijd om actie te ondernemen.</div>
        </div>
      )}

      <FacturenTable rows={rows} orgs={{}} orgLand={{}} onVerstuur={setVerstuurFor}
        showVerstuur={tab === "wachtrij" || tab === "alle"} loading={loading} onMailed={reloadAll} />

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="facturen-paginatie">
          <span className="text-muted-foreground">{totaal} facturen · pagina {page} / {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="factuur-vorige-pagina">Vorige</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="factuur-volgende-pagina">Volgende</Button>
          </div>
        </div>
      )}

      <VerstuurDialog factuur={verstuurFor} open={!!verstuurFor}
        onOpenChange={(o)=>{ if (!o) setVerstuurFor(null); }}
        onSent={reloadAll} />
    </div>
  );
}
